import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Pipeline } from '../../types/pipeline';

interface DeleteConfirmationModalProps {
  pipeline: Pipeline;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  pipeline,
  onConfirm,
  onCancel,
}) => {
  const hasWorkflows = pipeline.workflow_count > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="delete-confirmation-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete Pipeline?
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {hasWorkflows ? (
            // Error message: workflows exist
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-400 font-medium">
                  Cannot delete this pipeline
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                  <strong>{pipeline.workflow_count}</strong> workflow
                  {pipeline.workflow_count === 1 ? ' is' : 's are'} currently using this pipeline.
                </p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                To delete <strong>"{pipeline.name}"</strong>, you must first reassign or delete
                the workflows that are using it.
              </p>
            </div>
          ) : (
            // Confirmation message: no workflows
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete{' '}
                <strong className="text-gray-900 dark:text-white">"{pipeline.name}"</strong>?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This will set the pipeline status to <strong>DISABLED</strong>. It will no longer
                appear in the default list or be available for new workflows.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                This action can be reversed by changing the status back to ACTIVE or DRAFT in the
                pipeline editor.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
          {hasWorkflows ? (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              data-testid="ok-button"
            >
              OK
            </button>
          ) : (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                data-testid="confirm-button"
              >
                Delete Pipeline
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

